// frontend/src/context/CartContext.jsx
// UPDATED Jan 15, 2026: B15 - Added updateCartItemTimeWindow function

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Track if we've loaded from localStorage to prevent race condition
  const hasLoadedRef = useRef(false);

  // Load cart from localStorage on mount or when user changes
  useEffect(() => {
    if (user) {
      setIsLoading(true);
      const savedCart = localStorage.getItem(`cart_${user.id}`);
      if (savedCart) {
        try {
          const parsed = JSON.parse(savedCart);
          setCartItems(parsed);
        } catch (error) {
          console.error('Error loading cart:', error);
          setCartItems([]);
        }
      } else {
        setCartItems([]);
      }
      // Mark as loaded AFTER setting cart items
      hasLoadedRef.current = true;
      setIsLoading(false);
    } else {
      // No user - clear cart and mark as loaded
      setCartItems([]);
      hasLoadedRef.current = true;
      setIsLoading(false);
    }
  }, [user]);

  // Save cart to localStorage whenever it changes
  // ONLY after initial load to prevent race condition
  useEffect(() => {
    // Don't save until we've loaded first
    if (!hasLoadedRef.current) {
      return;
    }
    
    if (user) {
      if (cartItems.length > 0) {
        localStorage.setItem(`cart_${user.id}`, JSON.stringify(cartItems));
      } else {
        // Only clear if we've already loaded (prevents clearing on mount)
        localStorage.removeItem(`cart_${user.id}`);
      }
    }
  }, [cartItems, user]);

  // Reset loaded flag when user changes (logs out/in)
  useEffect(() => {
    return () => {
      hasLoadedRef.current = false;
    };
  }, [user?.id]);

  // Add item to cart
  const addToCart = (item) => {
    setCartItems(prevItems => {
      // Check if item already exists in cart
      const existingItemIndex = prevItems.findIndex(
        cartItem => cartItem.id === item.id && cartItem.groupId === item.groupId
      );

      if (existingItemIndex > -1) {
        // Update quantity if item exists
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity = item.quantity || 1;
        
        addNotification({
          type: 'success',
          title: 'Cart Updated',
          message: `Updated ${item.title} quantity to ${updatedItems[existingItemIndex].quantity}`,
          showToast: true
        });
        
        return updatedItems;
      } else {
        // Add new item
        const newItem = {
          ...item,
          quantity: item.quantity || 1,
          timeWindow: item.timeWindow || 'THREE_DAY', // Default time window
          addedAt: new Date().toISOString()
        };
        
        addNotification({
          type: 'success',
          title: 'Added to Cart',
          message: `${item.title || item.name || 'Item'} added to your cart`,
          showToast: true
        });
        
        return [...prevItems, newItem];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (itemId, groupId) => {
    setCartItems(prevItems => {
      const updatedItems = prevItems.filter(
        item => !(item.id === itemId && item.groupId === groupId)
      );
      
      addNotification({
        type: 'info',
        title: 'Item Removed',
        message: 'Item removed from your cart',
        showToast: true
      });
      
      return updatedItems;
    });
  };

  // Update item quantity
  const updateQuantity = (itemId, groupId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId, groupId);
      return;
    }

    setCartItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === itemId && item.groupId === groupId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      return updatedItems;
    });
  };

  // B15: Update time window for a cart item
  const updateCartItemTimeWindow = (itemId, groupId, newTimeWindow) => {
    setCartItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        // Match by itemId (could be id or productId)
        const isMatch = (item.id === itemId || item.productId === itemId) && 
                        (groupId ? item.groupId === groupId : true);
        
        if (isMatch) {
          return { ...item, timeWindow: newTimeWindow };
        }
        return item;
      });
      return updatedItems;
    });
  };

  // Update all items to same time window
  const updateAllTimeWindows = (newTimeWindow) => {
    setCartItems(prevItems => {
      const updatedItems = prevItems.map(item => ({
        ...item,
        timeWindow: newTimeWindow
      }));
      
      addNotification({
        type: 'success',
        title: 'Time Windows Updated',
        message: `All items set to ${newTimeWindow.replace('_', '-').toLowerCase()} delivery`,
        showToast: true
      });
      
      return updatedItems;
    });
  };

  // Clear entire cart
  const clearCart = () => {
    setCartItems([]);
    if (user) {
      localStorage.removeItem(`cart_${user.id}`);
    }
    
    addNotification({
      type: 'info',
      title: 'Cart Cleared',
      message: 'Your cart has been cleared',
      showToast: true
    });
  };

  // Calculate cart totals
  const getCartTotals = () => {
    const subtotal = cartItems.reduce((total, item) => {
      const itemPrice = item.currentPrice || item.price || 0;
      const quantity = item.quantity || 1;
      
      // Apply bulk discount if applicable
      let discount = 0;
      if (quantity >= 5) discount = 0.05;
      else if (quantity >= 3) discount = 0.03;
      
      const discountedPrice = itemPrice * (1 - discount);
      return total + (discountedPrice * quantity);
    }, 0);

    const totalTokens = cartItems.reduce((total, item) => {
      const baseTokens = item.tokenReward || 0;
      const quantity = item.quantity || 1;
      
      // Apply bulk bonus
      let bonus = 1;
      if (quantity >= 5) bonus = 1.5;
      else if (quantity >= 3) bonus = 1.25;
      
      return total + Math.floor(baseTokens * quantity * bonus);
    }, 0);

    const totalItems = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

    // Get unique time windows in cart
    const timeWindows = [...new Set(cartItems.map(item => item.timeWindow || 'THREE_DAY'))];

    return {
      subtotal,
      totalTokens,
      totalItems,
      itemCount: cartItems.length,
      timeWindows,
      hasMixedTimeWindows: timeWindows.length > 1
    };
  };

  // Check if item is in cart
  const isInCart = (itemId, groupId) => {
    return cartItems.some(item => item.id === itemId && item.groupId === groupId);
  };

  // Get cart item by id
  const getCartItem = (itemId, groupId) => {
    return cartItems.find(item => item.id === itemId && item.groupId === groupId);
  };

  // Get items grouped by time window
  const getItemsByTimeWindow = () => {
    const grouped = {};
    cartItems.forEach(item => {
      const tw = item.timeWindow || 'THREE_DAY';
      if (!grouped[tw]) {
        grouped[tw] = [];
      }
      grouped[tw].push(item);
    });
    return grouped;
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateCartItemTimeWindow, // B15: New function
    updateAllTimeWindows,     // B15: New function
    clearCart,
    getCartTotals,
    isInCart,
    getCartItem,
    getItemsByTimeWindow,     // B15: New function
    isLoading
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};